import { Component, computed, DestroyRef, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UpsellsModalService } from '@app/features/upsells/upsells-modal/upsells-modal.service';
import {
  UpsellOrder,
  UpsellRequestStatus,
  UpsellsService
} from '@app/features/upsells/upsells.service';
import { Reservation } from '@app/shared/interfaces';
import { PusherService } from '@app/shared/services/pusher/pusher.service';
import { finalize } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'sbnb-upsells-status',
  templateUrl: './upsells-status.component.html',
  styleUrls: ['./upsells-status.component.scss'],
})
export class UpsellsStatusComponent implements OnInit {
  @Input() reservation: Reservation;

  readonly UpsellRequestStatus = UpsellRequestStatus;

  loading = signal<boolean>(false);
  upsellOrders = signal<UpsellOrder[]>([]);

  approvedUpsells = computed(() => {
    return this.upsellOrders()
      .flatMap((order) => order.products)
      .filter((product) => ! product.requires_approval || product.upsell_request_status === UpsellRequestStatus.Approved)
  });
  pendingUpsellRequests = computed(() => {
    return this.upsellOrders()
      .flatMap((order) => order.products)
      .filter((product) => product.requires_approval && product.upsell_request_status === UpsellRequestStatus.Pending)
  });
  deniedUpsells = computed(() => {
    return this.upsellOrders()
      .flatMap((order) => order.products)
      .filter((product) => product.requires_approval && product.upsell_request_status === UpsellRequestStatus.Denied)
  });

  constructor(
    private upsellsService: UpsellsService,
    private upsellsModalService: UpsellsModalService,
    private pusherService: PusherService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.#fetchUpsells();
    this.#registerForUpsellsUpdates();
  }

  public openUpsellsModal() {
    this.upsellsModalService.open({
      reservationUuid: this.reservation.uuid,
      initialMode: 'overview',
    });
  }

  #fetchUpsells() {
    if (!this.reservation?.uuid) {
      console.error('Cannot refresh upsells: reservation UUID is missing');
      return;
    }

    this.loading.set(true);

    this.upsellsService
      .fetchUpsellsForReservation(this.reservation.uuid)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (upsells) => {
          this.upsellOrders.set(upsells);
        },
        error: (error) => {
          console.error('Failed to fetch upsells:', error);
        },
      });
  }

  #registerForUpsellsUpdates(): void {
    this.pusherService.reservationUpsellsUpdate$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        console.log('Pusher service: reservationUpsellsUpdate', data?.reservation_uuid, this.reservation?.uuid);
        // Check if the update is for the current reservation
        if (data?.reservation_uuid === this.reservation?.uuid) {
          this.#fetchUpsells();
        }
      });
  }
}
